import { z } from 'zod';
import { ResourceType } from '@cotera/api';
import { Ty } from '@cotera/era';
import type {
  NodeHandler,
  PositionedNodeHandler,
} from '../dg/document/handlers/types';
import type { MarkHandler } from '../dg/document/marks/types';
import type { GroupHandler } from '../dg/document/handlers/group-handler';
import type { MessageHandler } from '../dg/document/handlers/message-handler';
import { BuildExplorer } from '../dg/commit/build-explorer';
import { HandlerContainer } from '../dg/document/handler-container';
import { DocumentPromptMarkdownRenderer } from '../dg/document/prompt-markdown-renderer';
import {
  isTipTapTextNode,
  type ContentNode,
  type DocumentState,
  type TextNode,
} from '../dg/document/document-types';
import {
  normalizeJsonNode,
  responseShapeToShorthand,
} from '../dg/document/json-node';
import {
  INPUT_SHAPE_NODE_TYPE,
  parseInputShapeFields,
} from '../dg/document/handlers/input-shape';
import type { WorkspaceRenderBundle } from '../dg/document/workspace-render-bundle';
import {
  buildWorkspaceRenderBundle,
  type WorkspaceDomainLoader,
} from './workspace-prefetch';
import {
  agentIdToAgentToolKey,
  COCO_DATASET_READ_TOOL_KEY,
} from './coco-tools';

const mentionTypeEnum = z
  .enum(['tool', 'document', 'expr', 'agent', 'column', 'resource', 'input'])
  .optional()
  .catch(undefined);

export type WorkspacePromptMentionKind =
  | 'tool'
  | 'document'
  | 'expr'
  | 'agent'
  | 'column'
  | 'resource'
  | 'input'
  | undefined;

/** Coco rich-editor input mention primitive (matches client `CocoInputPrimitiveType`). */
export type WorkspacePromptInputPrimitive =
  | 'string'
  | 'number'
  | 'boolean'
  | 'super';

export type WorkspacePromptMention = {
  readonly id: string;
  readonly label: string;
  readonly type: WorkspacePromptMentionKind;
  readonly referenceId?: string;
  /**
   * When the mention payload is `t: "resource"` with structured `data.data`,
   * the resolved workspace resource (agent, document, folder, trigger, …).
   */
  readonly resource?: {
    readonly resourceType: ResourceType;
    readonly resourceId: string;
  };
  /**
   * When the mention payload is `t: "input"` (Coco agent/document prompt inputs).
   */
  readonly input?: {
    readonly inputKey: string;
    readonly inputType: WorkspacePromptInputPrimitive;
  };
};

function isResourceType(value: string): value is ResourceType {
  return (Object.values(ResourceType) as string[]).includes(value);
}

const WORKSPACE_PROMPT_INPUT_TYPES = new Set<WorkspacePromptInputPrimitive>([
  'string',
  'number',
  'boolean',
  'super',
]);

const isWorkspacePromptInputPrimitive = (
  value: string
): value is WorkspacePromptInputPrimitive =>
  WORKSPACE_PROMPT_INPUT_TYPES.has(value as WorkspacePromptInputPrimitive);

function parseWorkspacePromptMention(
  attrs: Record<string, unknown> | undefined
): Omit<WorkspacePromptMention, never> {
  const id =
    attrs !== undefined && typeof attrs['id'] === 'string'
      ? attrs['id']
      : 'unknown';
  const label =
    attrs !== undefined && typeof attrs['label'] === 'string'
      ? attrs['label']
      : id;
  let mentionType: string | undefined;
  let resource: WorkspacePromptMention['resource'];
  let input: WorkspacePromptMention['input'];
  if (attrs !== undefined && attrs['data'] !== undefined) {
    try {
      const data =
        typeof attrs['data'] === 'string'
          ? JSON.parse(attrs['data'])
          : attrs['data'];
      mentionType =
        typeof data === 'object' && data !== null
          ? (data as { t?: string; type?: string }).t ??
            (data as { t?: string; type?: string }).type
          : undefined;
      const inner = (data as { data?: unknown })?.data;
      if (
        typeof inner === 'object' &&
        inner !== null &&
        'resourceType' in inner &&
        'resourceId' in inner
      ) {
        const rt = String((inner as { resourceType: string }).resourceType);
        const rid = String((inner as { resourceId: string }).resourceId);
        if (isResourceType(rt)) {
          resource = { resourceType: rt, resourceId: rid };
        }
      }
      if (
        mentionType === 'input' &&
        typeof inner === 'object' &&
        inner !== null
      ) {
        const ik = (inner as { inputKey?: unknown }).inputKey;
        const it = (inner as { inputType?: unknown }).inputType;
        if (
          typeof ik === 'string' &&
          typeof it === 'string' &&
          isWorkspacePromptInputPrimitive(it)
        ) {
          input = { inputKey: ik, inputType: it };
        }
      }
    } catch {
      if (typeof attrs['data'] === 'string') {
        mentionType = attrs['data'];
      }
    }
  }
  const referenceId =
    attrs !== undefined && typeof attrs['referenceId'] === 'string'
      ? attrs['referenceId']
      : undefined;
  return {
    id,
    label,
    type: mentionTypeEnum.parse(mentionType) as WorkspacePromptMentionKind,
    referenceId,
    resource,
    input,
  };
}

/** Maps Coco UI input primitives to era shorthand (`number` → `float`). */
const inputPrimitiveToShorthand = (
  t: WorkspacePromptInputPrimitive
): Ty.Shorthand => {
  switch (t) {
    case 'string':
      return 'string';
    case 'number':
      return 'float';
    case 'boolean':
      return 'boolean';
    case 'super':
      return 'super';
    default: {
      const _exhaustive: never = t;
      return _exhaustive;
    }
  }
};

/** A declared agent input, from the `input-shape` block or a legacy `@input` mention. */
export type WorkspacePromptInputField = {
  readonly name: string;
  readonly type: WorkspacePromptInputPrimitive;
  readonly description: string | null;
};

/** Depth-first search for the first node of `type`. */
function findFirstNode(
  nodes: readonly (ContentNode | TextNode)[] | undefined,
  type: string
): ContentNode | null {
  if (!Array.isArray(nodes)) {
    return null;
  }
  for (const node of nodes) {
    if (typeof node !== 'object' || node === null) {
      continue;
    }
    if (Array.isArray(node)) {
      const found = findFirstNode(node, type);
      if (found !== null) {
        return found;
      }
      continue;
    }
    if (isTipTapTextNode(node)) {
      continue;
    }
    const cn = node as ContentNode;
    if (cn.type === type) {
      return cn;
    }
    const found = findFirstNode(cn.content, type);
    if (found !== null) {
      return found;
    }
  }
  return null;
}

function collectMentionsFromNodes(
  nodes: readonly (ContentNode | TextNode)[] | undefined,
  out: WorkspacePromptMention[]
): void {
  if (!Array.isArray(nodes)) {
    return;
  }
  for (const node of nodes) {
    if (typeof node !== 'object' || node === null) {
      continue;
    }
    if (Array.isArray(node)) {
      collectMentionsFromNodes(node, out);
      continue;
    }
    if (isTipTapTextNode(node)) {
      continue;
    }
    const cn = node as ContentNode;
    if (cn.type === 'mention') {
      const attrs = cn.attrs as Record<string, unknown> | undefined;
      out.push(parseWorkspacePromptMention(attrs));
    }
    collectMentionsFromNodes(cn.content, out);
  }
}

function mentionToToolKeys(mention: WorkspacePromptMention): readonly string[] {
  if (mention.type === 'tool') {
    const k = (mention.referenceId ?? mention.id).trim();
    if (k.length > 0) {
      return [k];
    }
    return [];
  }
  if (mention.type === 'agent') {
    return [agentIdToAgentToolKey(mention.referenceId ?? mention.id)];
  }
  if (mention.resource !== undefined) {
    if (mention.resource.resourceType === ResourceType.Agent) {
      return [agentIdToAgentToolKey(mention.resource.resourceId)];
    }
    // A mentioned dataset is declared in the prompt by name and columns only, so
    // the read tool is what makes its rows reachable. Write/edit stay behind the
    // `coco/dataset-editing` skill — naming a dataset is not asking to change it.
    if (mention.resource.resourceType === ResourceType.Dataset) {
      return [COCO_DATASET_READ_TOOL_KEY];
    }
  }
  return [];
}

export { formatWorkspaceInputMentionMarkdown } from '../dg/document/workspace-mention-branch';

/**
 * Compiles TipTap {@link DocumentState} to LLM markdown using domain-backed
 * {@link WorkspaceRenderBundle} resolution (no {@link BuildExplorer} graph).
 * Reuses the same TipTap handlers as the data graph editor.
 *
 * Coco `t: "input"` mentions are rendered inline via
 * {@link formatWorkspaceInputMentionMarkdown} in the shared {@link MentionHandler}.
 */
export class WorkspacePromptEditor {
  private readonly promptMarkdown: DocumentPromptMarkdownRenderer;

  constructor(
    private readonly state: DocumentState,
    private readonly workspaceDomainLoader: WorkspaceDomainLoader,
    nodeHandlers?: (NodeHandler | PositionedNodeHandler)[],
    markHandlers?: MarkHandler[],
    groupHandlers?: GroupHandler[],
    messageHandlers?: MessageHandler[]
  ) {
    const handlers = new HandlerContainer(
      nodeHandlers,
      markHandlers,
      groupHandlers
    );
    this.promptMarkdown = new DocumentPromptMarkdownRenderer(
      handlers,
      messageHandlers ?? []
    );
  }

  getState(): DocumentState {
    return this.state;
  }

  hasContent(): boolean {
    return this.promptMarkdown.hasContent(this.state);
  }

  hasBodyContent(): boolean {
    return this.promptMarkdown.hasBodyContent(this.state);
  }

  /**
   * All `mention` nodes in this document (depth-first), with parsed mention `type`
   * and optional workspace {@link WorkspacePromptMention.resource} payload.
   */
  references(): readonly WorkspacePromptMention[] {
    const out: WorkspacePromptMention[] = [];
    collectMentionsFromNodes(this.state.content, out);
    return out;
  }

  /**
   * References from this document and recursively mentioned workspace documents.
   * Cycles are ignored after first visit.
   */
  async referencesRecursive(): Promise<readonly WorkspacePromptMention[]> {
    const out: WorkspacePromptMention[] = [];
    const queue: DocumentState[] = [this.state];
    const visitedDocumentIds = new Set<string>();

    while (queue.length > 0) {
      const state = queue.shift();
      if (state === undefined) {
        break;
      }
      const local: WorkspacePromptMention[] = [];
      collectMentionsFromNodes(state.content, local);
      out.push(...local);

      const nestedDocumentIds: string[] = [];
      for (const mention of local) {
        if (
          mention.resource !== undefined &&
          mention.resource.resourceType === ResourceType.Document
        ) {
          nestedDocumentIds.push(mention.resource.resourceId);
        }
      }

      for (const docId of nestedDocumentIds) {
        if (visitedDocumentIds.has(docId)) {
          continue;
        }
        visitedDocumentIds.add(docId);
        const nested = await this.workspaceDomainLoader.loadDocument(docId);
        if (nested !== null) {
          queue.push(nested.editorState.getState());
        }
      }
    }

    return out;
  }

  /**
   * Unique tool keys referenced for LLM use: integration tools (`t: "tool"`) and
   * invocable saved agents (`t: "agent"` or agent-shaped `t: "resource"`), as `coco/agent-…` keys.
   */
  tools(): string[] {
    const keys = new Set<string>();
    for (const m of this.references()) {
      for (const key of mentionToToolKeys(m)) {
        keys.add(key);
      }
    }
    return [...keys];
  }

  /**
   * Tool keys from this document and recursively mentioned workspace documents.
   */
  async toolsRecursive(): Promise<string[]> {
    const keys = new Set<string>();
    for (const mention of await this.referencesRecursive()) {
      for (const key of mentionToToolKeys(mention)) {
        keys.add(key);
      }
    }
    return [...keys];
  }

  /**
   * Runtime inputs declared by this prompt: the `input-shape` block's fields, then
   * legacy inline `t: "input"` mentions for any key the block does not define
   * (duplicate mention keys keep the last occurrence in document order).
   */
  inputFields(): readonly WorkspacePromptInputField[] {
    const shapeNode = findFirstNode(this.state.content, INPUT_SHAPE_NODE_TYPE);
    const blockFields = parseInputShapeFields(shapeNode?.attrs);
    const blockKeys = new Set(blockFields.map((f) => f.name));

    const mentionFields = new Map<string, WorkspacePromptInputField>();
    for (const m of this.references()) {
      if (m.type !== 'input' || m.input === undefined) {
        continue;
      }
      if (blockKeys.has(m.input.inputKey)) {
        continue;
      }
      mentionFields.set(m.input.inputKey, {
        name: m.input.inputKey,
        type: m.input.inputType,
        description: null,
      });
    }

    return [...blockFields, ...mentionFields.values()];
  }

  /**
   * Shape of {@link inputFields} as a struct. With no inputs, returns `Ty.ty('string')`.
   */
  input(): Ty.ExtendedAttributeType {
    const inputFields = this.inputFields();
    if (inputFields.length === 0) {
      return Ty.ty('string');
    }
    const fields: Record<string, Ty.Shorthand> = {};
    for (const field of inputFields) {
      fields[field.name] = inputPrimitiveToShorthand(field.type);
    }
    return Ty.structOf(fields);
  }

  /**
   * When the bundle is already built (e.g. shared across several documents).
   */
  toPromptMarkdown(
    workspace: WorkspaceRenderBundle,
    mode?: 'simple' | 'expression'
  ): string {
    return this.promptMarkdown.render(this.state, {
      graph: BuildExplorer.empty(),
      mode,
      workspace,
    });
  }

  /**
   * Resolves mentions via the constructor-bound {@link WorkspaceDomainLoader},
   * then renders in simple markdown mode (workspace prompts are not compiled as expression SQL).
   */
  async toMarkdownPrompt(): Promise<string> {
    const workspace = await buildWorkspaceRenderBundle(
      this.state,
      this.workspaceDomainLoader
    );
    return this.toPromptMarkdown(workspace, 'simple');
  }

  /**
   * Expected LLM output type from an optional `response-shape` node, matching
   * `EditorState#getOutputTy` in the data-graph document editor. With no shape (or no
   * `jsonNode`), returns plain `string`.
   */
  getOutputTy(): Ty.ExtendedAttributeType {
    const responseShapeNode = findFirstNode(
      this.state.content,
      'response-shape'
    );
    if (responseShapeNode === null) {
      return Ty.ty('super');
    }
    const jsonNode = responseShapeNode.attrs?.['jsonNode'];
    if (jsonNode === undefined || jsonNode === null) {
      return Ty.ty('super');
    }
    // Repair the stored shape first: it is an opaque node attr, is often LLM
    // authored, and `responseShapeToShorthand` throws on a malformed one.
    return responseShapeToShorthand(normalizeJsonNode(jsonNode));
  }
}

export type { WorkspaceDomainLoader } from './workspace-prefetch';
